import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Divider,
    Button,
    Grid,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Card,
    CardContent,
    Chip,
    Avatar,
    LinearProgress,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    CloudUpload,
    InsertDriveFile,
    CheckCircle,
    Delete,
    Description,
    Image as ImageIcon,
    PictureAsPdf,
    VideoLibrary,
    Edit,
    Close
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import axios from 'axios'
import swal from 'sweetalert';
import { useHistory } from "react-router-dom";

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const DocumentItem = ({ file, onDelete, onEdit, isMobile }) => {
    const getFileIcon = (type) => {
        if (type.includes('image')) return <ImageIcon color="primary" fontSize={isMobile ? 'small' : 'medium'} />;
        if (type.includes('pdf')) return <PictureAsPdf color="error" fontSize={isMobile ? 'small' : 'medium'} />;
        if (type.includes('video')) return <VideoLibrary color="secondary" fontSize={isMobile ? 'small' : 'medium'} />;
        if (type.includes('sheet') || type.includes('excel'))
            return <InsertDriveFile color="success" fontSize={isMobile ? 'small' : 'medium'} />;
        return <Description color="action" fontSize={isMobile ? 'small' : 'medium'} />;
    };

    return (
        <ListItem
            secondaryAction={
                <Box>
                    <IconButton
                        edge="end"
                        onClick={() => onDelete(file.name)}
                        size={isMobile ? 'small' : 'medium'}
                    >
                        <Delete color="error" fontSize={isMobile ? 'small' : 'medium'} />
                    </IconButton>
                </Box>
            }
            sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                mb: 1,
                bgcolor: 'background.paper',
                px: isMobile ? 1 : 2,
                py: isMobile ? 0.5 : 1
            }}
        >
            <ListItemIcon sx={{ minWidth: isMobile ? 36 : 56 }}>
                {getFileIcon(file.type)}
            </ListItemIcon>
            <ListItemText
                primary={
                    <Typography
                        variant={isMobile ? 'body2' : 'body1'}
                        sx={{ wordBreak: 'break-word' }}
                    >
                        {file.name}
                    </Typography>
                }
                secondary={
                    <>
                        <Box component="span" display="block" variant={isMobile ? 'caption' : 'body2'}>
                            {`${(file.size / 1024).toFixed(2)} KB`}
                        </Box>
                        {file.description && (
                            <Box
                                component="span"
                                display="block"
                                fontStyle="italic"
                                color="text.secondary"
                                variant={isMobile ? 'caption' : 'body2'}
                            >
                                {file.description}
                            </Box>
                        )}
                    </>
                }
                primaryTypographyProps={{
                    variant: isMobile ? 'body2' : 'body1',
                    fontWeight: 'medium'
                }}
                secondaryTypographyProps={{
                    variant: isMobile ? 'caption' : 'body2'
                }}
            />
            <Chip
                icon={<CheckCircle fontSize={isMobile ? 'small' : 'medium'} />}
                label={isMobile ? '' : "Uploaded"}
                color="success"
                size={isMobile ? 'small' : 'medium'}
                variant="outlined"
                sx={{ ml: isMobile ? 0.5 : 1 }}
            />
        </ListItem>
    );
};

const SpeakerUploadDocuments = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [files, setFiles] = useState([]);
    const history = useHistory();
    const eventid = localStorage.getItem('eventid');
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [editingFile, setEditingFile] = useState(null);
    const [description, setDescription] = useState('');
    const [setting, setSettings] = useState('');

    const handleFileChange = (event) => {
        const selectedFiles = Array.from(event.target.files).map(file => ({
            file,
            name: file.name,
            type: file.type,
            size: file.size,
            description: '',
            lastModified: file.lastModified
        }));

        setFiles(prevFiles => [...prevFiles, ...selectedFiles]);

        // Simulate upload progress
        if (selectedFiles.length > 0) {
            setIsUploading(true);
            let progress = 0;
            const interval = setInterval(() => {
                progress += 10;
                setUploadProgress(progress);
                if (progress >= 100) {
                    clearInterval(interval);
                    setIsUploading(false);
                }
            }, 200);
        }
    };

    const handleDeleteFile = (fileName) => {
        setFiles(prevFiles => prevFiles.filter(file => file.name !== fileName));
    };

    const handleEditFile = (file) => {
        setEditingFile(file);
        setDescription(file.description || '');
    };

    const handleSaveDescription = () => {
        setFiles(prevFiles =>
            prevFiles.map(file =>
                file.name === editingFile.name
                    ? { ...file, description }
                    : file
            )
        );
        setEditingFile(null);
        setDescription('');
    };

    const documentTypes = [
        { name: 'Speaker Photo', format: 'JPG, PNG (Max 5MB)' },
        { name: 'Company Logo', format: 'JPG, PNG, SVG (Max 5MB)' },
        { name: 'Passport Copy', format: 'PDF, JPG (Max 10MB)' },
        { name: 'Presentation Slides', format: 'PDF, PPT, Excel (Max 20MB)' },
    ];

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            const convertToBase64 = (file) => {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(file);
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = (error) => reject(error);
                });
            };

            const fileDataPromises = files.map(async (fileObj) => {
                const base64 = await convertToBase64(fileObj.file || fileObj);
                return {
                    name: (fileObj.file || fileObj).name,
                    type: (fileObj.file || fileObj).type,
                    size: (fileObj.file || fileObj).size,
                    content: base64,
                    description: fileObj.description || '',
                };
            });

            const fileData = await Promise.all(fileDataPromises);

            const data = {
                description, // from state or form input
                files: fileData,
            };

            if (!data.description) {

                alert("Please enter a description before submitting.");
                setIsSubmitting(false);
                return; // stop submission

            }

            // Get CSRF cookie for Sanctum
            await axios.get('/sanctum/csrf-cookie');

            const res = await axios.post(`/api/speaker/documents?eventid=${eventid}`, data, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (res.data.status === 200) {
                axios.get(`/api/get-setting/${eventid}`)
                    .then(response => {
                        const settingsData = response.data.settings;
                        setSettings(settingsData);

                    })
                    .catch(error => {
                        setIsSubmitting(false);
                        console.error('Error fetching email settings:', error);
                    });
                setIsSubmitting(false);

                swal('Success', res.data.message, 'success');
                history.push(`/admin/conference/speaker-dashbord/eventid`);
            } else {
                swal('Error', 'Something went wrong', 'error');
            }
        } catch (error) {
            setIsSubmitting(false);
            console.error('Error submitting documents:', error);
            swal('Error', 'Upload failed. Try again.', 'error');
        }
    };

    const staticChange = eventid == 68;

    return (
        <Box sx={{
            maxWidth: 800,
            mx: 'auto',
            pt: isMobile ? 10 : 20,
            px: isMobile ? 1 : 3
        }}>
            {/* Heading Section */}
            <Card sx={{ mb: 3, boxShadow: isMobile ? 1 : 3 }}>
                <Button sx={{
                    float: 'right',
                    marginTop: '2%',
                    ":hover": {
                        color: "white"
                    }
                }}
                    color='success'
                    size='small'
                    variant='contained'
                    onClick={() => { history.goBack() }}>
                    Back
                </Button>
                <CardContent>
                    <Typography
                        variant={isMobile ? 'h6' : 'h5'}
                        component="h2"
                        gutterBottom
                        sx={{ fontWeight: 600 }}
                    >
                        Upload Your Documents
                    </Typography>

                    <Typography
                        variant={isMobile ? 'body2' : 'body1'}
                        color="text.secondary"
                        paragraph
                    >
                        Please upload all required documents for your {staticChange ? "hospital" : "speaker"} participation. Ensure files are clear and meet the specified requirements.
                        You can add descriptions to each file to identify its purpose.
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    {
                        !staticChange && (
                            <Grid container spacing={isMobile ? 1 : 2}>
                                {documentTypes.map((doc, index) => (
                                    <Grid item xs={12} sm={6} key={index}>
                                        <Paper variant="outlined" sx={{ p: isMobile ? 1 : 2, height: '100%' }}>
                                            <Typography variant={isMobile ? 'body2' : 'subtitle2'} sx={{ fontWeight: 600 }}>
                                                {doc.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Format: {doc.format}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        )
                    }


                </CardContent>
            </Card>

            {/* Upload Section */}
            <Card sx={{ mb: 3, boxShadow: isMobile ? 1 : 3 }}>
                <CardContent>
                    <Box
                        sx={{
                            border: '2px dashed #1976d2',
                            borderRadius: 2,
                            p: isMobile ? 2 : 4,
                            textAlign: 'center',
                            bgcolor: 'rgba(25, 118, 210, 0.04)',
                            cursor: 'pointer',
                            '&:hover': {
                                bgcolor: 'rgba(25, 118, 210, 0.08)'
                            }
                        }}
                    >
                        <CloudUpload sx={{
                            fontSize: isMobile ? 36 : 48,
                            color: 'primary.main',
                            mb: 1
                        }} />
                        <Typography variant={isMobile ? 'body1' : 'h6'} gutterBottom>
                            Drag and drop files here
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            or
                        </Typography>
                        <Button
                            component="label"
                            variant="contained"
                            startIcon={<CloudUpload fontSize={isMobile ? 'small' : 'medium'} />}
                            sx={{ mt: 1 }}
                            size={isMobile ? 'small' : 'medium'}
                        >
                            {isMobile ? 'Select' : 'Select Files'}
                            <VisuallyHiddenInput
                                type="file"
                                multiple
                                onChange={handleFileChange}
                                accept="image/*,.pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx"
                            />

                            {/* <VisuallyHiddenInput
                                type="file"
                                multiple
                                onChange={handleFileChange}
                                accept="image/*,.pdf,.ppt,.pptx,.doc,.docx"
                            /> */}
                        </Button>
                        <Typography variant="caption" display="block" sx={{ mt: 2 }}>
                            Supported formats: JPG, PNG, PDF, PPT, DOC (Max file size: 20MB)
                        </Typography>
                    </Box>

                    {isUploading && (
                        <Box sx={{ mt: 2 }}>
                            <LinearProgress
                                variant="determinate"
                                value={uploadProgress}
                                sx={{ height: 8, borderRadius: 4 }}
                            />
                            <Typography variant="caption" display="block" textAlign="right">
                                {uploadProgress}% complete
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Uploaded Documents Section */}
            <Card sx={{ boxShadow: isMobile ? 1 : 3 }}>
                <CardContent>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2
                    }}>
                        <Typography variant={isMobile ? 'body1' : 'h6'} sx={{ fontWeight: 600 }}>
                            Uploaded Documents
                        </Typography>
                        <Chip
                            label={isMobile ? `${files.length}` : `${files.length} files`}
                            color="primary"
                            size={isMobile ? 'small' : 'medium'}
                            avatar={<Avatar sx={{ width: 24, height: 24 }}>{files.length}</Avatar>}
                        />
                    </Box>

                    {files.length === 0 ? (
                        <Paper variant="outlined" sx={{ p: isMobile ? 2 : 4, textAlign: 'center' }}>
                            <Description sx={{
                                fontSize: isMobile ? 36 : 48,
                                color: 'text.disabled',
                                mb: 1
                            }} />
                            <Typography variant={isMobile ? 'body2' : 'body1'} color="text.secondary">
                                No documents uploaded yet
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Upload your files to see them listed here
                            </Typography>
                        </Paper>
                    ) : (
                        <List dense>
                            {files.map((file, index) => (
                                <DocumentItem
                                    key={index}
                                    file={file}
                                    onDelete={handleDeleteFile}
                                    onEdit={handleEditFile}
                                    isMobile={isMobile}
                                />
                            ))}
                        </List>
                    )}
                    <TextField
                        autoFocus
                        margin="dense"
                        label="File description"
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={isMobile ? 2 : 3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="E.g. 'This is my passport copy', 'Presentation slides for keynote'"
                        sx={{ mt: 2 }}
                    />
                    {files.length > 0 && (
                        <Box sx={{
                            mt: 2,
                            display: 'flex',
                            justifyContent: isMobile ? 'center' : 'flex-end'
                        }}>
                            <Button
                                variant="contained"
                                color="primary"
                                size={isMobile ? 'medium' : 'large'}
                                startIcon={<CheckCircle fontSize={isMobile ? 'small' : 'medium'} />}
                                sx={{
                                    minWidth: isMobile ? '100%' : 200,
                                    py: isMobile ? 1 : undefined
                                }}
                                onClick={handleSubmit}
                                disabled={isSubmitting} >
                                {isMobile || isSubmitting ? 'Submit' : 'Submit All Documents'}

                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default SpeakerUploadDocuments;